<script setup lang="ts">
// 폰 위장 화면: 메모장·계산기(Free) / 날씨·할 일(Pro). design/prototype.html 포팅.
// 숨기기는 쉽게(부모가 표시), 다시 열기는 비밀번호로만.
//  - 메모/날씨/할일: 제목(또는 기온)을 길게 누르면 비밀번호 입력칸이 나타난다 → 비번/PIN 입력 시 즉시 해제
//  - 계산기: 진짜 계산되고, PIN(또는 숫자 비번) 입력 후 '=' 를 누르면 열린다
import { nextTick, reactive, ref, watch } from 'vue'
import type { MobileCoverId } from '../../stores/ui'
import { useVaultStore } from '../../stores/vault'
import { icon } from '../../lib/icons'

const props = defineProps<{ cover: MobileCoverId; pin: string }>()
const emit = defineEmits<{ unlock: [] }>()
const vault = useVaultStore()

/* ── 비밀번호로 열기(메모/날씨/할일 공통): 입력값이 PIN·앱 비번과 맞으면 즉시/디바운스 해제 ── */
const revealed = ref(false)
const pw = ref('')
let verifyT: number | undefined
watch(pw, (val) => {
  clearTimeout(verifyT)
  const typed = val.trim()
  if (!typed) return
  if (typed === props.pin) { emit('unlock'); return } // PIN: 즉시
  verifyT = window.setTimeout(async () => { if (await vault.verifyPassword(typed)) emit('unlock') }, 400)
})
async function pwEnter() {
  const typed = pw.value.trim()
  if (typed && (typed === props.pin || await vault.verifyPassword(typed))) emit('unlock')
}

/* ── 길게 누르기 → 비밀번호 입력칸 노출 ── */
const holding = ref(false)
let holdT: number | undefined
function holdStart(e: Event) {
  e.preventDefault(); holding.value = true
  holdT = window.setTimeout(() => {
    holding.value = false; revealed.value = true
    nextTick(() => (document.querySelector('.mcover-pw') as HTMLInputElement | null)?.focus())
  }, 650)
}
function holdEnd() { clearTimeout(holdT); holding.value = false }

/* ── 계산기(진짜 동작) ── */
const calc = reactive({ disp: '0', acc: null as number | null, op: null as string | null, fresh: true, typed: '' })
function calcApply(a: number, b: number, op: string) {
  return op === '+' ? a + b : op === '−' ? a - b : op === '×' ? a * b : op === '÷' ? (b === 0 ? NaN : a / b) : b
}
function calcFmt(n: number) { if (!isFinite(n)) return '오류'; const t = String(+n.toPrecision(10)); return t.length > 11 ? n.toExponential(4) : t }
function calcReset() { Object.assign(calc, { disp: '0', acc: null, op: null, fresh: true, typed: '' }) }
async function calcKey(k: string) {
  if (/^[0-9.]$/.test(k)) {
    calc.typed += k
    if (calc.fresh) { calc.disp = k === '.' ? '0.' : k; calc.fresh = false }
    else if (!(k === '.' && calc.disp.includes('.')) && calc.disp.length < 11) calc.disp = calc.disp === '0' && k !== '.' ? k : calc.disp + k
  } else if (k === 'C') { calcReset() }
  else if (k === '±') calc.disp = calcFmt(-parseFloat(calc.disp))
  else if (k === '%') calc.disp = calcFmt(parseFloat(calc.disp) / 100)
  else if (k === '=') {
    if (calc.op === null && calc.typed && (calc.typed === props.pin || await vault.verifyPassword(calc.typed))) { calcReset(); emit('unlock'); return }
    if (calc.op !== null) { calc.disp = calcFmt(calcApply(calc.acc as number, parseFloat(calc.disp), calc.op)); calc.op = null; calc.acc = null }
    calc.fresh = true; calc.typed = ''
  } else {
    if (calc.op !== null && !calc.fresh) calc.disp = calcFmt(calcApply(calc.acc as number, parseFloat(calc.disp), calc.op))
    calc.acc = parseFloat(calc.disp); calc.op = k; calc.fresh = true; calc.typed = ''
  }
}
const CALC_KEYS: Array<{ k: string; c?: string }> = [
  { k: 'C', c: 'fn' }, { k: '±', c: 'fn' }, { k: '%', c: 'fn' }, { k: '÷', c: 'op' },
  { k: '7' }, { k: '8' }, { k: '9' }, { k: '×', c: 'op' },
  { k: '4' }, { k: '5' }, { k: '6' }, { k: '−', c: 'op' },
  { k: '1' }, { k: '2' }, { k: '3' }, { k: '+', c: 'op' },
  { k: '0', c: 'zero' }, { k: '.' }, { k: '=', c: 'op' },
]

/* ── 할 일(체크 동작) ── */
const todos = reactive([
  { t: '세탁소 셔츠 찾기', done: true }, { t: '엄마 생신 선물 주문', done: false },
  { t: '자동차 보험 갱신 확인', done: false }, { t: '헬스장 PT 예약 변경', done: false },
])
const todoLeft = () => todos.filter(t => !t.done).length

const MEMOS = [
  { b: '장보기', s: '오후 6:12 · 우유, 계란, 두부, 대파' },
  { b: '택배 반송 주소', s: '어제 · 경기도 성남시 분당구…' },
  { b: '10월 운동', s: '월요일 · 스쿼트 5x5, 런닝 3km' },
  { b: '읽을 책', s: '10. 14. · 불편한 편의점, 아몬드' },
  { b: '와이파이 비번', s: '10. 2. · 거실 공유기 뒷면' },
]
const HOURS = [
  { h: '지금', i: 'partly', t: '18°' }, { h: '16시', i: 'sun', t: '19°' }, { h: '17시', i: 'sun', t: '19°' },
  { h: '18시', i: 'partly', t: '17°' }, { h: '19시', i: 'cloud', t: '16°' }, { h: '20시', i: 'cloud', t: '15°' },
]
const DAYS = [
  { d: '오늘', i: 'partly', lo: '12°', hi: '21°' }, { d: '금', i: 'rain', lo: '13°', hi: '18°' },
  { d: '토', i: 'sun', lo: '11°', hi: '22°' }, { d: '일', i: 'sun', lo: '12°', hi: '23°' },
]
</script>

<template>
  <div class="mcover" :data-cover="cover">
    <!-- 메모장 -->
    <section v-if="cover === 'memo'" class="mc-memo">
      <div class="memo-top">
        <button class="memo-title hold" :class="{ holding }"
          @pointerdown="holdStart" @pointerup="holdEnd" @pointerleave="holdEnd" @contextmenu.prevent>
          메모장
          <svg class="hold-ring" viewBox="0 0 26 26" aria-hidden="true"><circle cx="13" cy="13" r="11" /></svg>
        </button>
        <span class="memo-edit" v-html="icon('plus')"></span>
      </div>
      <div class="memo-search">
        <input v-if="revealed" class="mcover-pw" type="password" inputmode="text"
          placeholder="메모 검색" autocomplete="off" v-model="pw" @keydown.enter.prevent="pwEnter" />
        <span v-else>메모 검색</span>
      </div>
      <div class="memo-list">
        <div v-for="(m, i) in MEMOS" :key="i" class="memo-item"><b>{{ m.b }}</b><span>{{ m.s }}</span></div>
      </div>
      <div class="memo-count">{{ MEMOS.length }}개의 메모</div>
    </section>

    <!-- 계산기 -->
    <section v-else-if="cover === 'calc'" class="mc-calc">
      <div class="calc-disp">{{ calc.disp }}</div>
      <div class="calc-keys">
        <button v-for="key in CALC_KEYS" :key="key.k" :class="key.c" @click="calcKey(key.k)">{{ key.k }}</button>
      </div>
    </section>

    <!-- 날씨 -->
    <section v-else-if="cover === 'weather'" class="mc-weather">
      <div class="w-city">서울특별시</div>
      <button class="w-temp hold" :class="{ holding }"
        @pointerdown="holdStart" @pointerup="holdEnd" @pointerleave="holdEnd" @contextmenu.prevent>
        18°<svg class="hold-ring" viewBox="0 0 26 26" aria-hidden="true"><circle cx="13" cy="13" r="11" /></svg>
      </button>
      <div class="w-cond">구름 조금 · 최고 21° 최저 12°</div>
      <div v-if="revealed" class="w-pwbox">
        <input class="mcover-pw" type="password" placeholder="지역 검색" autocomplete="off" v-model="pw" @keydown.enter.prevent="pwEnter" />
      </div>
      <div class="w-card w-hours">
        <div v-for="(h, i) in HOURS" :key="i"><small>{{ h.h }}</small><span v-html="icon(h.i)"></span><b>{{ h.t }}</b></div>
      </div>
      <div class="w-card w-days">
        <div v-for="(d, i) in DAYS" :key="i"><span>{{ d.d }}</span><span v-html="icon(d.i)"></span><b>{{ d.lo }} / {{ d.hi }}</b></div>
      </div>
    </section>

    <!-- 할 일 -->
    <section v-else class="mc-todo">
      <button class="todo-title hold" :class="{ holding }"
        @pointerdown="holdStart" @pointerup="holdEnd" @pointerleave="holdEnd" @contextmenu.prevent>
        할 일
        <svg class="hold-ring" viewBox="0 0 26 26" aria-hidden="true"><circle cx="13" cy="13" r="11" /></svg>
      </button>
      <div class="todo-sub">오늘 · {{ todoLeft() }}개 남음</div>
      <div v-if="revealed" class="todo-pwbox">
        <input class="mcover-pw" type="password" placeholder="할 일 추가" autocomplete="off" v-model="pw" @keydown.enter.prevent="pwEnter" />
      </div>
      <ul class="todo-list">
        <li v-for="(t, i) in todos" :key="i" :class="{ done: t.done }" @click="t.done = !t.done"><i></i>{{ t.t }}</li>
      </ul>
      <div class="todo-add"><span v-html="icon('plus')"></span> 새로운 할 일</div>
    </section>
  </div>
</template>

<style scoped>
.mcover { position: fixed; inset: 0; z-index: 90; overflow-y: auto;
  padding: max(52px, calc(env(safe-area-inset-top) + 16px)) 16px calc(28px + env(safe-area-inset-bottom));
  background: var(--card); color: var(--ink); font-family: var(--font-ui); }
.mcover .ico { width: 24px; height: 24px; }

/* 길게 누르기 진행 링 */
.hold { position: relative; border: 0; background: none; color: inherit; font: inherit; cursor: default;
  user-select: none; -webkit-user-select: none; touch-action: none; padding: 4px 0; }
.hold-ring { position: absolute; right: -32px; top: 50%; width: 26px; height: 26px; margin-top: -13px; transform: rotate(-90deg); opacity: 0; transition: opacity .2s; }
.hold-ring circle { fill: none; stroke: currentColor; stroke-width: 3; stroke-linecap: round; stroke-dasharray: 69.1; stroke-dashoffset: 69.1; }
.hold.holding .hold-ring { opacity: .8; }
.hold.holding .hold-ring circle { stroke-dashoffset: 0; transition: stroke-dashoffset .65s linear; }
.mcover-pw { border: 0; background: none; font: inherit; color: inherit; outline: none; width: 100%; padding: 0; }

/* 메모장 */
.mc-memo { color: #202020; }
:root[data-theme="dark"] .mc-memo, [data-theme="dark"] .mcover { }
.memo-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.memo-title { font-size: 26px; font-weight: 700; }
.memo-edit { color: #3F7D5A; display: inline-flex; }
.memo-search { border-bottom: 1px solid var(--line); padding: 11px 0; color: var(--pencil); font-size: 15px; margin-bottom: 4px; }
.memo-search .mcover-pw { color: var(--ink); }
.memo-item { padding: 14px 0; border-bottom: 1px solid var(--line); }
.memo-item:last-child { border-bottom: 0; }
.memo-item b { display: block; font-weight: 600; font-size: 16px; }
.memo-item span { color: var(--pencil); font-size: 14px; }
.memo-count { text-align: center; color: var(--pencil); font-size: 12px; margin-top: 18px; }

/* 계산기 */
.mcover[data-cover="calc"] { background: #121417; color: #fff; display: flex; flex-direction: column; justify-content: flex-end; }
.calc-disp { text-align: right; font-size: 62px; font-weight: 300; padding: 0 8px 18px; font-variant-numeric: tabular-nums; overflow: hidden; white-space: nowrap; }
.calc-keys { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.calc-keys button { height: 64px; border: 0; border-radius: 20px; background: #2A2E35; color: #fff; font-size: 26px; cursor: pointer; transition: filter .1s, transform .1s; }
.calc-keys button:active { filter: brightness(1.4); transform: scale(.96); }
.calc-keys .fn { background: #4A505A; font-size: 22px; }
.calc-keys .op { background: #3B7D8C; }
.calc-keys .zero { grid-column: span 2; text-align: left; padding-left: 28px; }

/* 날씨 */
.mcover[data-cover="weather"] { background: #4F79A3; color: #fff; text-align: center; }
.w-city { font-size: 26px; font-weight: 500; }
.w-temp { font-size: 64px; font-weight: 300; line-height: 1.1; }
.w-temp .hold-ring { right: -26px; } .w-temp .hold-ring circle { stroke: #fff; }
.w-cond { font-size: 15px; opacity: .9; margin-bottom: 18px; }
.w-pwbox { max-width: 240px; margin: 0 auto 16px; background: rgba(255,255,255,.18); border-radius: 12px; padding: 10px 14px; }
.w-pwbox .mcover-pw { color: #fff; text-align: center; }
.w-pwbox .mcover-pw::placeholder { color: rgba(255,255,255,.7); }
.w-card { background: rgba(255,255,255,.14); border-radius: 18px; padding: 12px 14px; margin-bottom: 12px; text-align: left; }
.w-hours { display: flex; justify-content: space-between; }
.w-hours div { display: flex; flex-direction: column; align-items: center; gap: 6px; font-size: 13px; }
.w-hours .ico { width: 22px; height: 22px; }
.w-days div { display: grid; grid-template-columns: 44px 28px 1fr; align-items: center; gap: 10px; padding: 7px 0; font-size: 15px; border-top: 1px solid rgba(255,255,255,.18); }
.w-days div:first-child { border-top: 0; }
.w-days .ico { width: 20px; height: 20px; }
.w-days b { text-align: right; font-weight: 500; font-size: 14px; }

/* 할 일 */
.todo-title { font-size: 26px; font-weight: 700; }
.todo-sub { color: var(--pencil); font-size: 14px; margin-bottom: 16px; }
.todo-pwbox { border: 1px solid var(--line); border-radius: 12px; padding: 11px 14px; margin-bottom: 12px; }
.todo-list { list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--line); }
.todo-list li { display: flex; align-items: center; gap: 12px; padding: 14px 2px; border-bottom: 1px solid var(--line); font-size: 16px; cursor: pointer; }
.todo-list i { width: 22px; height: 22px; border-radius: 50%; border: 2px solid var(--pencil); flex: none; transition: .2s; }
.todo-list li.done { color: var(--pencil); text-decoration: line-through; }
.todo-list li.done i { background: #4F8A6B; border-color: #4F8A6B; box-shadow: inset 0 0 0 3px var(--card); }
.todo-add { color: #4F8A6B; padding: 14px 2px; font-size: 16px; display: flex; align-items: center; gap: 6px; }
.todo-add .ico { width: 16px; height: 16px; }
</style>
